/**
 * Live-Ticker: Zustand, Spielregeln und Einblendungen.
 *
 * Ein Event hat genau *einen* Ticker-Zustand. Welche Felder davon sichtbar
 * sind, entscheidet `mode` -- Fussball zeigt Tore und Spieluhr, Tennis
 * Saetze/Spiele/Punkte, ein Lauf die Leute auf der Strecke. Der Zustand ist
 * absichtlich flach: so laesst er sich in einem Rutsch verschicken und der
 * Wechsel der Sportart verliert nichts ausser dem, was ohnehin nicht passt.
 *
 * Gepflegt wird alles von Hand in der Regie. Eine spaetere Anbindung an einen
 * Datenanbieter wuerde genau diesen Zustand befuellen -- so wie eine
 * RaceResult-Anbindung nur die Startliste befuellen wuerde.
 */
import type { SportId } from './index.ts';

/* ------------------------------------------------------------------ Modus */

export type TickerMode = 'off' | 'match' | 'tennis' | 'race' | 'backyard' | 'plain';

export interface TickerModeInfo {
  id: TickerMode;
  label: string;
  hint: string;
}

export const TICKER_MODES: TickerModeInfo[] = [
  { id: 'off', label: 'Aus', hint: 'Kein Ticker auf der Event-Seite.' },
  { id: 'match', label: 'Spiel', hint: 'Zwei Mannschaften, Tore, laufende Spieluhr.' },
  { id: 'tennis', label: 'Tennis', hint: 'Sätze, Spiele, Punkte — inklusive Einstand und Tiebreak.' },
  { id: 'race', label: 'Strecke', hint: 'Wer ist unterwegs, Zwischenzeiten, Zieleinläufe.' },
  {
    id: 'backyard',
    label: 'Backyard',
    hint: 'Loop für Loop: Runden zählen, Strecke hochrechnen, Feld ausdünnen.',
  },
  { id: 'plain', label: 'Frei', hint: 'Zwei Zähler und ein freier Ticker — passt überall.' },
];

/** Womit ein Event startet, wenn die Regie nichts umstellt. */
export function defaultTickerMode(sport: SportId): TickerMode {
  switch (sport) {
    case 'fussball':
    case 'handball':
    case 'basketball':
    case 'volleyball':
      return 'match';
    case 'tennis':
      return 'tennis';
    case 'laufen':
    case 'radsport':
    case 'schwimmen':
    case 'wintersport':
      return 'race';
    default:
      return 'plain';
  }
}

/**
 * Welche Ticker-Arten zur Sportart passen. Ein Tor-Tracker bei einem Lauf
 * oder ein Zieleinlauf-Tracker bei Fussball waere sinnlos -- deshalb bietet
 * die Regie nur die Modi an, die zur Sportart des Events gehoeren.
 *
 * `off` (Ticker ganz abschalten) und `plain` (zwei Zaehler, kein
 * Sport-Vokabular) passen dagegen auf jedes Event und stehen immer zur Wahl.
 *
 * `backyard` ist der Sonderfall in die andere Richtung: ein Backyard Ultra
 * ist eine Laufveranstaltung und sonst nichts -- der Modus taucht deshalb
 * ausschliesslich bei `laufen` in der Auswahl auf.
 */
export function allowedTickerModes(sport: SportId): TickerMode[] {
  const natural = defaultTickerMode(sport);
  return TICKER_MODES.map((m) => m.id).filter(
    (id) =>
      id === 'off' || id === 'plain' || id === natural || (id === 'backyard' && sport === 'laufen'),
  );
}

/* ------------------------------------------------------------------ Uhr */

/**
 * Spieluhr. Sie wird nicht hochgezaehlt, sondern gerechnet: `baseSeconds` ist
 * der Stand beim letzten Anhalten, `startedAt` der Moment des letzten Starts.
 * Dadurch stimmt die Anzeige auch nach einem Reconnect und ohne dass der
 * Server im Sekundentakt sendet.
 */
export interface TickerClock {
  running: boolean;
  baseSeconds: number;
  startedAt: number | null;
  /**
   * Geplante Dauer des Wettkampfs in Sekunden, oder null. Damit weiss die
   * Anzeige, wie weit es noch ist -- „47:20 von 2:00:00". Sie stoppt die Uhr
   * nicht, sie ist nur der Bezugspunkt.
   */
  targetSeconds: number | null;
}

export const stoppedClock = (seconds = 0, targetSeconds: number | null = null): TickerClock => ({
  running: false,
  baseSeconds: seconds,
  startedAt: null,
  targetSeconds,
});

export function clockSeconds(clock: TickerClock, now: number = Date.now()): number {
  const extra = clock.running && clock.startedAt ? (now - clock.startedAt) / 1000 : 0;
  return Math.max(0, Math.floor(clock.baseSeconds + extra));
}

/**
 * Liest eine von Hand getippte Zeit. Erlaubt sind `90` (Minuten), `45:00`
 * (mm:ss) und `2:00:00` (h:mm:ss) -- null, wenn daraus keine Zeit wird.
 *
 * Eine einzelne Zahl als Minuten zu lesen ist die Konvention, die zur Regie
 * passt: „stell die Uhr auf 45" heisst nie 45 Sekunden.
 */
export function parseClock(text: string): number | null {
  const parts = text.trim().split(':');
  if (parts.length > 3) return null;
  const nums = parts.map((p) => Number(p.trim()));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null;
  if (nums.length === 1) return Math.round(nums[0] * 60);
  if (nums.length === 2) return Math.round(nums[0] * 60 + nums[1]);
  return Math.round(nums[0] * 3600 + nums[1] * 60 + nums[2]);
}

/** `07:24` bzw. `1:07:24`, sobald es ueber eine Stunde geht. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}

/** Spielminute im Fussball-Sinn: die 0. Sekunde ist die 1. Minute. */
export function matchMinute(clock: TickerClock, now?: number): string {
  const s = clockSeconds(clock, now);
  return s === 0 ? "0'" : `${Math.floor(s / 60) + 1}'`;
}

/* ---------------------------------------------------------------- Seiten */

export interface TickerSide {
  name: string;
  /** Kuerzel fuers Overlay -- da ist wenig Platz. */
  short: string;
  /** Tore bzw. gewonnene Saetze. */
  score: number;
  /** Tennis: Spiele im laufenden Satz. */
  games: number;
  /** Tennis: `0` `15` `30` `40` `A` -- im Tiebreak eine Zahl. */
  points: string;
}

/** Macht aus „SV Blau-Weiß Lingen" ein „SVB". */
export function shortName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '—';
  if (words.length >= 2) {
    return words
      .slice(0, 3)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
  }
  return words[0].slice(0, 3).toUpperCase();
}

export const newSide = (name: string): TickerSide => ({
  name,
  short: shortName(name),
  score: 0,
  games: 0,
  points: '0',
});

/* ------------------------------------------------------------- Strecke */

export type RaceEntryStatus = 'onCourse' | 'finished' | 'dnf';

export const raceStatusLabel: Record<RaceEntryStatus, string> = {
  onCourse: 'unterwegs',
  finished: 'im Ziel',
  dnf: 'aufgegeben',
};

/** Eine Person auf der Strecke, wie die Regie sie gerade sieht. */
export interface RaceEntry {
  id: string;
  bib: string;
  name: string;
  /** Letzter Messpunkt, z. B. „km 5". */
  checkpoint: string;
  /** Zeit dort, als Text — die Regie tippt sie so, wie sie sie sieht. */
  time: string;
  status: RaceEntryStatus;
  /** Platz im Ziel, sonst null. */
  place: number | null;
  updatedAt: number;
  /*
   * Backyard-Strecke: wo diese Person gerade ungefaehr ist, siehe
   * `backyardPositions` und `loopPrognosis`. Alles optional -- ein normaler
   * Lauf braucht es nicht.
   */
  /** „Runde ⌀" aus RACE RESULT in Sekunden. Gilt nur, solange die Zeitnahme antwortet. */
  rrLapSeconds?: number;
  /**
   * Die letzten Runden, die die Regie mit „zurück" gemessen hat -- aelteste
   * zuerst, eine mehr als `LAP_HISTORY` (fuer „rückgängig").
   */
  lapTimes?: LapTime[];
  /**
   * Rundenzeit aus Staenden von vor `lapTimes` (eine Messung oder aus einem
   * Antippen abgeleitet). Wird nur noch gelesen, solange nichts gemessen ist.
   */
  loopSeconds?: number;
  /** Alt, wird nicht mehr gelesen -- „rückgängig" nimmt heute die Messung aus `lapTimes`. */
  loopSecondsBefore?: number;
  /** In dieser Stunde der Uhr (0 = erster Loop) schon zurueck am Start. */
  backHour?: number;
  /** Von der Regie gesetzt: in Stunde `hour`, `at` Sekunden nach deren Beginn, beim Anteil `fraction`. */
  fix?: CourseFix;
}

/** Eine gemessene Runde: in Stunde `hour` der Uhr nach `seconds` Sekunden zurueck. */
export interface LapTime {
  hour: number;
  seconds: number;
}

export interface CourseFix {
  hour: number;
  at: number;
  fraction: number;
}

/* ------------------------------------------------------------ Backyard */

/**
 * Backyard Ultra: alle rennen dieselbe Runde, immer wieder, bis nur noch
 * eine Person uebrig ist. Interessant sind deshalb nicht Platzierungen,
 * sondern drei Zahlen -- wie weit, wie viele Runden, wie viele noch dabei.
 *
 * Die Rundenlaenge steht in *Metern* und nicht in Kilometern: `6.7 * 3`
 * ergibt in Gleitkomma `20.099999999999998`, `6700 * 3` dagegen exakt 20100.
 * Gerechnet wird also ganzzahlig, und erst die Anzeige macht daraus km.
 */
export interface BackyardState {
  /** Laenge einer Runde in Metern. */
  lapMeters: number;
  /** Abgeschlossene Runden. */
  loops: number;
  /** Wie viele noch im Rennen sind -- die Regie zaehlt mit +1/-1 mit. */
  runners: number;
  /**
   * Auf welcher vollen Stunde der Uhr die Loops zuletzt abgeglichen wurden.
   * Bei einem Backyard *ist* jede volle Stunde ein Loop, deshalb folgen die
   * Loops der Uhr -- siehe `backyardAtHour`. Fehlt bei Staenden von vor der
   * Automatik.
   */
  countedHours?: number;
  /**
   * Standard-Rundenzeit in Sekunden fuer die Streckenkarte -- gilt fuer alle,
   * deren eigene Rundenzeit noch nicht gemessen ist. Fehlt bei alten Staenden.
   */
  loopSeconds?: number;
}

/** Die klassische Backyard-Runde: 4,167 Meilen. */
export const BACKYARD_LAP_METERS = 6706;
export const MAX_LAP_METERS = 200_000;
export const MAX_LOOPS = 500;
export const MAX_RUNNERS = 9999;

/** Womit die Punkte loslaufen, solange niemand gemessen ist: 50 Minuten. */
export const DEFAULT_LOOP_SECONDS = 50 * 60;

export const defaultBackyard = (): BackyardState => ({
  lapMeters: BACKYARD_LAP_METERS,
  loops: 0,
  runners: 0,
  countedHours: 0,
  loopSeconds: DEFAULT_LOOP_SECONDS,
});

/* ------------------------------------------------ Backyard: Streckenkarte */

/** Schneller ist niemand ueber eine Backyard-Runde -- kuerzer gemessen war ein Fehlgriff. */
export const MIN_LOOP_SECONDS = 15 * 60;
/** Laenger darf eine Runde nicht dauern, sonst ist man raus. */
export const MAX_LOOP_SECONDS = 60 * 60;
/**
 * Bis hierhin wandert ein Punkt von selbst. Den Rest -- „ist zurueck" --
 * weiss nur die Regie; ein Punkt, der von allein im Ziel stuende, saehe aus
 * wie eine Meldung, die niemand gemacht hat.
 */
export const MAX_ESTIMATED_FRACTION = 0.97;

export const clampLoopSeconds = (seconds: number): number =>
  Math.min(MAX_LOOP_SECONDS, Math.max(MIN_LOOP_SECONDS, Math.round(seconds)));

/** Aus so vielen gemessenen Runden wird der Schnitt gebildet. */
export const LAP_HISTORY = 3;

/**
 * Woher die Rundenzeit eines Punkts kommt: aus RACE RESULT, aus den Runden,
 * die die Regie mit „zurück" gemessen hat, oder die Standardzeit.
 */
export type LoopSource = 'raceresult' | 'measured' | 'standard';

export interface LoopPrognosis {
  /** Erwartete Rundenzeit in Sekunden. */
  seconds: number;
  source: LoopSource;
  /** Bei `measured`: aus wie vielen Runden der Schnitt ist. */
  laps: number;
}

/**
 * Wie lange diese Person fuer die laufende Runde voraussichtlich braucht.
 *
 * 1. Die „Runde ⌀" aus RACE RESULT -- solange die Zeitnahme antwortet
 *    (`raceResult`, siehe `resultsPaceLive`).
 * 2. Sonst der Schnitt der letzten drei Runden, die die Regie mit „zurück"
 *    gemessen hat. Sind es erst eine oder zwei, eben aus diesen.
 * 3. Sonst die Standard-Rundenzeit.
 *
 * Die eigenen Messungen laufen also auch mit, solange RACE RESULT die Zeiten
 * liefert -- faellt die Zeitnahme aus, rechnen die Punkte mit ihnen weiter.
 */
export function loopPrognosis(entry: RaceEntry, standard: number, raceResult: boolean): LoopPrognosis {
  if (raceResult && entry.rrLapSeconds) return { seconds: entry.rrLapSeconds, source: 'raceresult', laps: 0 };
  const laps = (entry.lapTimes ?? []).slice(-LAP_HISTORY);
  if (laps.length) {
    const seconds = Math.round(laps.reduce((sum, lap) => sum + lap.seconds, 0) / laps.length);
    return { seconds, source: 'measured', laps: laps.length };
  }
  if (entry.loopSeconds) return { seconds: entry.loopSeconds, source: 'measured', laps: 1 };
  return { seconds: standard, source: 'standard', laps: 0 };
}

/** Ein Punkt auf der Streckenkarte. */
export interface CoursePosition {
  entryId: string;
  bib: string;
  name: string;
  /** Anteil der Runde, 0 = Start, 1 = zurueck. */
  fraction: number;
  /** Zurueck am Start und wartet auf die naechste volle Stunde. */
  back: boolean;
  /** Mit welcher Rundenzeit gerechnet wird. */
  loopSeconds: number;
  /** Woher diese Rundenzeit kommt. */
  source: LoopSource;
  /** Bei `measured`: aus wie vielen Runden der Schnitt ist. */
  laps: number;
  /** Ob die Rundenzeit zu dieser Person gehoert -- sonst die Standardzeit. */
  measured: boolean;
}

/**
 * Wo die Laeufer gerade ungefaehr sind -- geschaetzt, nicht gemessen.
 *
 * Bei einem Backyard startet jede Runde zur vollen Stunde der Uhr. Wer eine
 * Rundenzeit von 48 Minuten erwarten laesst, ist 24 Minuten nach der vollen
 * Stunde also etwa auf halber Strecke. Die Rundenzeit ist die Prognose aus
 * `loopPrognosis`. Ein Antippen auf der Karte setzt den Punkt und rechnet von
 * dort mit derselben Prognose weiter (`fix`). Wer zurueck ist, steht bis zur
 * naechsten Stunde am Start.
 *
 * Gerechnet wird in jedem Browser selbst, aus Uhr und Ticker -- es geht also
 * nichts im Sekundentakt ueber die Leitung.
 */
export function backyardPositions(
  ticker: TickerState,
  now: number = Date.now(),
  raceResult = false,
): CoursePosition[] {
  const t = clockSeconds(ticker.clock, now);
  const hour = Math.floor(t / 3600);
  const elapsed = t - hour * 3600;
  const standard = ticker.backyard.loopSeconds ?? DEFAULT_LOOP_SECONDS;
  return ticker.entries
    .filter((e) => e.status === 'onCourse')
    .map((e) => {
      const { seconds: loopSeconds, source, laps } = loopPrognosis(e, standard, raceResult);
      const base = { entryId: e.id, bib: e.bib, name: e.name, loopSeconds, source, laps, measured: source !== 'standard' };
      if (e.backHour === hour) return { ...base, fraction: 1, back: true };
      const fix = e.fix && e.fix.hour === hour && e.fix.at <= elapsed ? e.fix : null;
      const fraction = fix ? fix.fraction + (elapsed - fix.at) / loopSeconds : elapsed / loopSeconds;
      return { ...base, fraction: Math.min(MAX_ESTIMATED_FRACTION, Math.max(0, fraction)), back: false };
    });
}

/**
 * Die Loops, wenn die Uhr auf der vollen Stunde `hour` steht. Gezaehlt wird
 * der Unterschied zur zuletzt abgeglichenen Stunde, vor wie zurueck: eine
 * versehentlich vorgestellte Uhr bringt Loops und Strecke mit, das
 * Zurueckstellen nimmt sie wieder weg. Was die Regie von Hand korrigiert hat,
 * bleibt als Abstand erhalten. Ohne `countedHours` (Stand von vor der
 * Automatik) wird nur der Merker gesetzt, nichts gezaehlt.
 */
export function backyardAtHour(b: BackyardState, hour: number): BackyardState {
  if (b.countedHours === hour) return b;
  const counted = b.countedHours ?? hour;
  return { ...b, loops: Math.min(MAX_LOOPS, Math.max(0, b.loops + hour - counted)), countedHours: hour };
}

/**
 * Die Loops zur vollen Stunde `hour`, wenn RACE RESULT sie fuehrt. Die Uhr
 * zaehlt dann nicht mit, sie sichert nur nach unten ab: zur vollen Stunde n
 * ist Loop n vorbei -- wer bis dahin nicht zurueck ist, ist nach den
 * Backyard-Regeln raus. Mindestens `hour` Loops sind es also, auch wenn die
 * Zeitnahme jemanden noch nicht als DNF fuehrt oder gerade gar nicht
 * antwortet. Zurueck nimmt die Uhr nichts; der Merker laeuft mit, damit sie
 * beim Umschalten auf „von Hand" ohne Sprung weiterzaehlt.
 */
export function backyardFloorAtHour(b: BackyardState, hour: number): BackyardState {
  const loops = Math.min(MAX_LOOPS, Math.max(b.loops, hour));
  if (loops === b.loops && b.countedHours === hour) return b;
  return { ...b, loops, countedHours: hour };
}

/**
 * Die Meldung zu einem geschafften Loop. Eine Stelle fuer den Wortlaut: live
 * zur vollen Stunde, von Hand gezaehlt und beim Nachbauen des Verlaufs aus
 * RACE RESULT steht dasselbe da -- und `unpostLoops` erkennt alle drei.
 */
export function backyardLoopText(loop: number, lapMeters: number, runners: number): string {
  const field = runners ? `, ${runners} noch im Rennen` : '';
  return `Loop ${loop} geschafft — ${formatDistance(Math.max(0, lapMeters) * loop)} gesamt${field}.`;
}

/** Gesamtstrecke nach `loops` Runden -- die Zahl, die ins Bild gehoert. */
export const backyardTotalMeters = (b: BackyardState): number =>
  Math.max(0, b.lapMeters) * Math.max(0, b.loops);

/**
 * Meter als Strecke, wie man sie ansagt: `6,7 km`, `20,1 km`, `800 m`.
 * Bis zu drei Nachkommastellen, damit die offizielle Runde (6,706 km) nicht
 * auf einen krummen Wert gerundet wird -- Nullen fallen dabei weg.
 */
export function formatDistance(meters: number): string {
  const m = Math.max(0, Math.round(meters));
  // Die Null ist kein Meterwert, sondern „noch nichts gelaufen" -- sie in
  // Metern zu schreiben laese sich im Regie-Pult als „0 M".
  if (m === 0) return '0 km';
  if (m < 1000) return `${m} m`;
  const km = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(m / 1000);
  return `${km} km`;
}

/**
 * Liest eine getippte Streckenlaenge und gibt Meter zurueck. Erlaubt sind
 * `6,7`, `6.7`, `6,7 km` (alles Kilometer) und `800 m` (Meter) -- null,
 * wenn daraus keine Strecke wird.
 *
 * Das Komma ist der Normalfall: eine deutsche Regie tippt `6,7`, nicht `6.7`.
 */
export function parseDistance(text: string): number | null {
  const raw = text.trim().toLowerCase().replace(',', '.');
  const match = /^([0-9]*\.?[0-9]+)\s*(km|m)?$/.exec(raw);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  const meters = match[2] === 'm' ? value : value * 1000;
  return Math.min(MAX_LAP_METERS, Math.round(meters));
}

/* ---------------------------------------------------------- Ticker-Feed */

export type TickerEntryKind = 'goal' | 'card' | 'sub' | 'period' | 'point' | 'split' | 'finish' | 'note';

export interface TickerEntryInfo {
  id: TickerEntryKind;
  label: string;
  icon: string;
}

export const TICKER_KINDS: TickerEntryInfo[] = [
  { id: 'goal', label: 'Tor', icon: '⚽' },
  { id: 'card', label: 'Karte', icon: '🟨' },
  { id: 'sub', label: 'Wechsel', icon: '🔁' },
  { id: 'period', label: 'Abschnitt', icon: '⏱' },
  { id: 'point', label: 'Punkt', icon: '🎾' },
  { id: 'split', label: 'Zwischenzeit', icon: '⏲' },
  { id: 'finish', label: 'Ziel', icon: '🏁' },
  { id: 'note', label: 'Notiz', icon: '📝' },
];

export const tickerKindIcon = (kind: TickerEntryKind): string =>
  TICKER_KINDS.find((k) => k.id === kind)?.icon ?? '•';

/** Ein Eintrag im chronologischen Ticker. */
export interface TickerEntry {
  id: string;
  kind: TickerEntryKind;
  /** Zeitmarke im Wettkampf: `67'`, `km 5`, `Satz 2`. */
  mark: string;
  text: string;
  /** Stand nach diesem Ereignis, z. B. `2:1`. */
  score: string | null;
  ts: number;
}

/* --------------------------------------------------------------- Zustand */

export interface TickerState {
  mode: TickerMode;
  /** Zeile ueber dem Ticker, z. B. „Halbfinale". */
  headline: string;
  /** Abschnitt: „1. Halbzeit", „Satz 2", „läuft". */
  period: string;
  /** Freier Text fuers Notiz-Widget. */
  note: string;
  sides: [TickerSide, TickerSide];
  /** Tennis: wer aufschlaegt. */
  serving: 0 | 1 | null;
  tiebreak: boolean;
  /** Abgeschlossene Saetze als [heim, gast]. */
  completedSets: [number, number][];
  clock: TickerClock;
  entries: RaceEntry[];
  /** Nur im Backyard-Modus sichtbar, aber immer gesetzt. */
  backyard: BackyardState;
  updatedAt: number;
}

export function emptyTicker(mode: TickerMode = 'off', home = 'Heim', away = 'Gast'): TickerState {
  return {
    mode,
    headline: '',
    period: '',
    note: '',
    sides: [newSide(home), newSide(away)],
    serving: null,
    tiebreak: false,
    completedSets: [],
    clock: stoppedClock(),
    entries: [],
    backyard: defaultBackyard(),
    updatedAt: Date.now(),
  };
}

/**
 * Zeigt dieser Ticker zwei Seiten mit Punktestand?
 *
 * Bei `match` und `tennis` gehoeren zwei Seiten zum Sport dazu. `plain`
 * bringt zwar zwei freie Zaehler mit -- aber nicht bei Streckensport: bei
 * einem Lauf gibt es kein Heim und Gast, und ein Zaehler mit „+ Tor" waere
 * dort schlicht falsch. Dort bleibt von `plain` der freie Ticker plus Uhr.
 */
export function showsSides(mode: TickerMode, sport: SportId): boolean {
  if (mode === 'match' || mode === 'tennis') return true;
  if (mode === 'plain') return defaultTickerMode(sport) !== 'race';
  return false;
}

/**
 * Zeigt dieser Ticker eine Strecken-Liste?
 *
 * Beim Backyard genauso wie beim normalen Lauf: wer noch dabei ist und wer
 * ausgestiegen, ist dort sogar die eigentliche Geschichte.
 */
export const showsRaceList = (mode: TickerMode): boolean => mode === 'race' || mode === 'backyard';

/**
 * Zeitmarke, die ein neues Ereignis bekommt, wenn die Regie keine tippt.
 * Fussball zaehlt Minuten, alles andere die reine Uhr.
 */
export function currentMark(state: TickerState, now?: number): string {
  if (state.mode === 'match') return matchMinute(state.clock, now);
  if (state.mode === 'tennis') return state.period || 'Satz ' + (state.completedSets.length + 1);
  const seconds = clockSeconds(state.clock, now);
  return seconds > 0 ? formatClock(seconds) : '';
}

/** `2:1` -- fuer die Ereigniszeile im Feed. */
export function scoreLine(state: TickerState): string | null {
  if (state.mode === 'tennis') return `${state.sides[0].score}:${state.sides[1].score}`;
  if (state.mode === 'match' || state.mode === 'plain') {
    return `${state.sides[0].score}:${state.sides[1].score}`;
  }
  return null;
}

/* ------------------------------------------------------------ Tennisregeln */

const LADDER = ['0', '15', '30', '40'];

/**
 * Ein Punkt fuer eine Seite -- mit Einstand, Vorteil, Spiel, Satz und
 * Tiebreak. Reine Funktion: rein alter Zustand, raus neuer.
 *
 * Der Aufschlagwechsel passiert hier nur beim Spielwechsel. Im Tiebreak
 * wechselt der Aufschlag alle zwei Punkte -- das stellt die Regie von Hand,
 * dafuer lohnt die Regel im POC nicht.
 */
export function awardTennisPoint(state: TickerState, side: 0 | 1): TickerState {
  const other = side === 0 ? 1 : 0;
  const me = { ...state.sides[side] };
  const you = { ...state.sides[other] };
  let tiebreak = state.tiebreak;
  let completedSets = state.completedSets;
  let serving = state.serving;
  let gameWon = false;

  if (tiebreak) {
    const mine = (Number(me.points) || 0) + 1;
    const theirs = Number(you.points) || 0;
    me.points = String(mine);
    gameWon = mine >= 7 && mine - theirs >= 2;
  } else if (me.points === 'A') {
    gameWon = true;
  } else if (you.points === 'A') {
    // Einstand: der Vorteil des Gegners verfaellt.
    you.points = '40';
  } else if (me.points === '40') {
    if (you.points === '40') me.points = 'A';
    else gameWon = true;
  } else {
    const next = LADDER.indexOf(me.points) + 1;
    me.points = LADDER[next] ?? '40';
  }

  if (gameWon) {
    me.games += 1;
    me.points = '0';
    you.points = '0';

    const setWon = tiebreak || (me.games >= 6 && me.games - you.games >= 2);
    if (setWon) {
      me.score += 1;
      completedSets = [
        ...completedSets,
        (side === 0 ? [me.games, you.games] : [you.games, me.games]) as [number, number],
      ];
      me.games = 0;
      you.games = 0;
      tiebreak = false;
    } else if (me.games === 6 && you.games === 6) {
      tiebreak = true;
    }
    if (serving !== null) serving = serving === 0 ? 1 : 0;
  }

  const sides: [TickerSide, TickerSide] = side === 0 ? [me, you] : [you, me];
  return { ...state, sides, tiebreak, completedSets, serving, updatedAt: Date.now() };
}

/**
 * true, wenn der Punktestand einen eigenen Namen hat -- Einstand, Vorteil,
 * Tiebreak. Nur dann lohnt es, ihn neben dem Scoreboard auszuschreiben.
 */
export function isSpecialGameScore(state: TickerState): boolean {
  const [a, b] = state.sides;
  return state.tiebreak || a.points === 'A' || b.points === 'A' || (a.points === '40' && b.points === '40');
}

/** Punktestand des laufenden Spiels, z. B. `40:30` oder `Einstand`. */
export function gameScoreLabel(state: TickerState): string {
  const [a, b] = state.sides;
  if (state.tiebreak) return `Tiebreak ${a.points}:${b.points}`;
  if (a.points === 'A') return 'Vorteil ' + (a.short || a.name);
  if (b.points === 'A') return 'Vorteil ' + (b.short || b.name);
  if (a.points === '40' && b.points === '40') return 'Einstand';
  return `${a.points}:${b.points}`;
}

/* ------------------------------------------------------------ Einblendungen */

/** Was die Regie ueber das Hauptbild legen kann. */
export type WidgetId = 'scoreboard' | 'clock' | 'lastEvent' | 'list' | 'note' | 'backyard' | 'weather' | 'course';

/**
 * Feste Positionen -- relativ zum Bild, damit es auf jedem Gerät gleich sitzt.
 *
 * `upper` und `lower` sind die beiden Baender ueber die volle Breite: unten
 * die Bauchbinde, oben das Gegenstueck dazu. Ein Band steht mittig und laesst
 * links und rechts Platz, damit es den Ecken nicht ins Gehege kommt.
 */
export type WidgetSlot = 'upper' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'lower';

/**
 * Wie gross eine Einblendung im Bild steht. Der Wert ist ein Faktor auf die
 * Schriftgroesse der Einblendungs-Ebene -- weil dort alles in em bemessen
 * ist, waechst und schrumpft das ganze Kaestchen mit.
 *
 * Fuenf Stufen statt drei: `sm`/`md`/`lg` sind unveraendert das, was sie
 * immer waren -- `xs` und `xl` kommen aussen dazu. Deshalb sieht ein Event,
 * das vorher eingestellt wurde, hinterher genauso aus wie vorher.
 */
export type WidgetSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface WidgetConfig {
  on: boolean;
  slot: WidgetSlot;
  size: WidgetSize;
  /**
   * Akzentfarbe der Einblendung als `#rrggbb` -- ohne Angabe das Rot, das
   * alle Einblendungen von Haus aus haben. Sie gehoert zum Stream, nicht zum
   * Browser: Zuschauer, Regie-Monitor und RTMP-Ausgang sehen dieselbe.
   */
  color?: string;
  /**
   * Nur Strecke: als Vollbild statt als Kaestchen. Das Hauptbild laeuft dann
   * klein in der Ecke `slot` weiter (bei einem Band unten rechts), `size` ist
   * seine Groesse. Ohne Angabe Vollbild -- siehe `courseIsFull`.
   */
  full?: boolean;
}

/** Steht die Streckenkarte als Vollbild? Ohne Angabe ja -- so ist sie besser zu lesen. */
export const courseIsFull = (config: WidgetConfig): boolean => config.full !== false;

/** Die Ecke fuer das Hauptbild, wenn die Streckenkarte im Vollbild steht. */
export const courseLiveSlot = (config: WidgetConfig): CornerSlot =>
  config.slot === 'upper' || config.slot === 'lower' ? 'bottomRight' : config.slot;

/** Gueltige Akzentfarbe einer Einblendung, klein geschrieben -- sonst null. */
export const parseWidgetColor = (value: unknown): string | null =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;

export type WidgetState = Record<WidgetId, WidgetConfig>;

export const WIDGETS: { id: WidgetId; label: string; hint: string }[] = [
  { id: 'scoreboard', label: 'Stand', hint: 'Kürzel und Punktzahl beider Seiten.' },
  { id: 'clock', label: 'Uhr', hint: 'Spielzeit bzw. Wettkampfuhr.' },
  { id: 'lastEvent', label: 'Letztes Ereignis', hint: 'Der neueste Ticker-Eintrag als Balken.' },
  { id: 'list', label: 'Liste', hint: 'Strecke: wer ist unterwegs. Sonst: die letzten Ereignisse.' },
  { id: 'note', label: 'Notiz', hint: 'Freier Text — Hinweis, Ankündigung, Danksagung.' },
  { id: 'backyard', label: 'Backyard', hint: 'Gesamtstrecke, Teilnehmerzahl und Loops.' },
  { id: 'weather', label: 'Wetter', hint: 'Wetter am Event-Ort — jetzt, die nächsten Stunden oder heute/morgen.' },
  {
    id: 'course',
    label: 'Strecke',
    hint: 'Backyard: die Runde mit den Startnummern — geschätzt aus der Rundenzeit. Als Vollbild mit dem Hauptbild klein in der Ecke, oder als Kästchen.',
  },
];

export const WIDGET_SLOTS: { id: WidgetSlot; label: string }[] = [
  { id: 'upper', label: 'oben Mitte' },
  { id: 'topLeft', label: 'oben links' },
  { id: 'topRight', label: 'oben rechts' },
  { id: 'bottomLeft', label: 'unten links' },
  { id: 'bottomRight', label: 'unten rechts' },
  { id: 'lower', label: 'Bauchbinde' },
];

/**
 * Positionen, die auch fuer ein Videofenster taugen -- also die vier Ecken.
 * Die beiden Baender fallen raus: dort liegt eine Einblendung ueber die
 * volle Breite, beides zusammen wuerde sich ueberlagern.
 */
export type CornerSlot = Exclude<WidgetSlot, 'lower' | 'upper'>;

export const CORNER_SLOTS: { id: CornerSlot; label: string }[] = [
  { id: 'topLeft', label: 'oben links' },
  { id: 'topRight', label: 'oben rechts' },
  { id: 'bottomLeft', label: 'unten links' },
  { id: 'bottomRight', label: 'unten rechts' },
];

/**
 * Wie die zweite Kamera im Bild steht: klein in einer Ecke oder
 * gleichberechtigt neben dem Hauptbild (Splitscreen). Welche Kamera die
 * zweite ist, bleibt dieselbe Wahl wie beim kleinen Fenster -- der
 * Splitscreen ist nur eine andere Anordnung derselben zwei Bilder.
 */
export type PipMode = 'pip' | 'split';

/** Aufteilung im Splitscreen. */
export type SplitLayout = 'half' | 'wide' | 'stack';

export const SPLIT_LAYOUTS: { id: SplitLayout; label: string; hint: string }[] = [
  { id: 'half', label: '50/50', hint: 'Beide Kameras gleich groß nebeneinander.' },
  { id: 'wide', label: '2/3 + 1/3', hint: 'Hauptbild breit, zweite Kamera schmal daneben.' },
  { id: 'stack', label: 'Übereinander', hint: 'Hauptbild oben, zweite Kamera darunter.' },
];

/**
 * Eine Haelfte des Bilds ist nicht mehr 16:9 -- nebeneinander fast
 * quadratisch, uebereinander sehr flach. `cover` fuellt sie und schneidet
 * dafuer ab (hochkant gehaltene Handys passen so gut hinein), `contain`
 * zeigt das ganze Bild und laesst daneben den Hintergrund sehen.
 */
export type SplitFit = 'cover' | 'contain';

export const SPLIT_FITS: { id: SplitFit; label: string; hint: string }[] = [
  { id: 'cover', label: 'Füllen', hint: 'Jede Hälfte ganz ausfüllen — die Ränder werden abgeschnitten.' },
  { id: 'contain', label: 'Ganz zeigen', hint: 'Ganzes Bild zeigen — daneben bleibt Hintergrund frei.' },
];

/** Wo und wie gross die zweite Kamera im Bild steht. */
export interface PipLayout {
  /** Ecke und Groesse -- gelten nur fuers kleine Fenster. */
  slot: CornerSlot;
  size: WidgetSize;
  mode: PipMode;
  split: SplitLayout;
  /** Getauscht: die zweite Kamera steht links bzw. oben. */
  swap: boolean;
  fit: SplitFit;
}

export const defaultPip = (): PipLayout => ({
  slot: 'bottomRight',
  size: 'md',
  mode: 'pip',
  split: 'half',
  swap: false,
  fit: 'cover',
});

export const WIDGET_SIZES: { id: WidgetSize; label: string; short: string }[] = [
  { id: 'xs', label: 'Sehr klein', short: 'XS' },
  { id: 'sm', label: 'Klein', short: 'S' },
  { id: 'md', label: 'Mittel', short: 'M' },
  { id: 'lg', label: 'Groß', short: 'L' },
  { id: 'xl', label: 'Sehr groß', short: 'XL' },
];

/**
 * Rang einer Groesse, klein nach gross. Gebraucht ueberall dort, wo aus
 * mehreren Einblendungen die groesste gesucht wird -- ein Vergleich auf der
 * Zeichenkette wuerde `xl` hinter `xs` einsortieren.
 */
export const widgetSizeRank = (size: WidgetSize): number =>
  WIDGET_SIZES.findIndex((s) => s.id === size);

/** Die groesste von mehreren Groessen; null, wenn keine dabei ist. */
export const largestSize = (sizes: WidgetSize[]): WidgetSize | null =>
  sizes.reduce<WidgetSize | null>(
    (biggest, size) => (!biggest || widgetSizeRank(size) > widgetSizeRank(biggest) ? size : biggest),
    null,
  );

export const defaultWidgets = (): WidgetState => ({
  scoreboard: { on: false, slot: 'topLeft', size: 'md' },
  clock: { on: false, slot: 'topRight', size: 'md' },
  lastEvent: { on: false, slot: 'lower', size: 'md' },
  list: { on: false, slot: 'bottomLeft', size: 'md' },
  note: { on: false, slot: 'lower', size: 'md' },
  // Die Backyard-Anzeige gehoert nach oben -- das Gegenteil der Bauchbinde.
  backyard: { on: false, slot: 'upper', size: 'md' },
  weather: { on: false, slot: 'topLeft', size: 'md' },
  course: { on: false, slot: 'bottomRight', size: 'md', full: true },
});

/* --------------------------------------------------- Nutzlasten fuers WS */

/** Felder, die die Regie direkt setzen darf. Alles optional, alles geprueft. */
export interface TickerBoardPatch {
  headline?: string;
  period?: string;
  note?: string;
  serving?: 0 | 1 | null;
  tiebreak?: boolean;
  sides?: [Partial<TickerSide>, Partial<TickerSide>];
}

/** Rundenlaenge, Rundenzahl und verbliebenes Feld -- alles einzeln setzbar. */
export interface BackyardPatch {
  lapMeters?: number;
  loops?: number;
  runners?: number;
  /** Standard-Rundenzeit der Streckenkarte in Sekunden. */
  loopSeconds?: number;
}

/**
 * Streckenkarte: `back` -- ist gerade zurueck, das misst die Rundenzeit;
 * `unback` nimmt es zurueck; `place` -- ist gerade hier (`fraction`, 0..1).
 */
export type CourseAction = 'back' | 'unback' | 'place';

export interface RaceEntryPatch {
  id?: string;
  bib?: string;
  name?: string;
  checkpoint?: string;
  time?: string;
  status?: RaceEntryStatus;
  place?: number | null;
}
